import { defineStore } from 'pinia';
import { useDataAnalysisStore } from '@/stores/dataAnaysisStore';
import { buildPeriodAnalysisRows } from '@/services/billrunsPeriodAnalysisService';
import {
  buildEnergyDataRequestsFromRows,
  submitEnergyDataRequests,
} from '@/services/billrunEnergyDataRequestsService';

const MAX_CACHE_ENTRIES = 10;
const pendingQueries = new Map();

export function parsePeriodPickerDate(value) {
  let dateValue = value;
  if (value && typeof value.toJSDate === 'function') {
    dateValue = value.toJSDate();
  }

  if (dateValue instanceof Date && !Number.isNaN(dateValue.getTime())) {
    return new Date(dateValue.getFullYear(), dateValue.getMonth(), dateValue.getDate());
  }
  if (typeof dateValue === 'string') {
    const isoDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateValue);
    const parsed = new Date(isoDateOnly ? `${dateValue}T00:00:00` : dateValue);
    if (!Number.isNaN(parsed.getTime())) {
      return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
    }
  }
  return null;
}

function dateKey(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function queryKey(communities, period) {
  const communityKeys = communities
    .map((community) => `${community.ecn}:${community.ecid}`)
    .sort();
  return `${communityKeys.join('|')}@${dateKey(period.dateStart)}:${dateKey(period.dateEnd)}`;
}

function selectionFingerprint(ecids, period) {
  if (!period) return '';
  return `${[...ecids].sort().join('|')}@${dateKey(period.dateStart)}:${dateKey(period.dateEnd)}`;
}

function buildRowVariants(results) {
  return {
    billingRelevant: buildPeriodAnalysisRows(results, { billingRelevantOnly: true }),
    all: buildPeriodAnalysisRows(results, { billingRelevantOnly: false }),
  };
}

function rowsForMode(rowVariants, billingRelevantOnly) {
  return billingRelevantOnly ? rowVariants.billingRelevant : rowVariants.all;
}

async function fetchCommunityResults(dataAnalysisStore, community, period) {
  const { ecn, ecid } = community;
  const { dateStart, dateEnd } = period;
  const [unclean, incorrectQh, incorrectCodes] = await Promise.all([
    dataAnalysisStore.getUncleanDataEntriesForCommunity(ecn, ecid, dateStart, dateEnd),
    dataAnalysisStore.getIncorrectCountofQh(ecn, ecid, dateStart, dateEnd),
    dataAnalysisStore.getIncorrectMeterCodes(ecn, ecid, dateStart, dateEnd),
  ]);
  if (![unclean, incorrectQh, incorrectCodes].every(Array.isArray)) {
    throw new Error(`Energiedaten-Analyse für ${ecn} konnte nicht geladen werden.`);
  }
  return { community, unclean, incorrectQh, incorrectCodes };
}

export const useBillrunsPeriodAnalysisStore = defineStore('billrunsPeriodAnalysis', {
  state: () => ({
    selectedCommunityEcids: [],
    dateRange: [],
    billingRelevantMeterCodesOnly: true,
    rows: [],
    rowVariants: { billingRelevant: [], all: [] },
    loading: false,
    hasFetched: false,
    error: null,
    cachedRowsByQuery: {},
    cacheOrder: [],
    energyRequestPreview: { requests: [], unresolvedMeterPoints: [], summary: null },
    energyRequestPreviewLoading: false,
    submitting: false,
    submissionProgress: { current: 0, total: 0 },
  }),

  getters: {
    selectedPeriod(state) {
      if (state.dateRange.length === 0) return null;
      const dateStart = parsePeriodPickerDate(state.dateRange[0]);
      const dateEnd = parsePeriodPickerDate(state.dateRange[state.dateRange.length - 1]);
      return dateStart && dateEnd ? { dateStart, dateEnd } : null;
    },
    invalidPeriod() {
      return Boolean(this.selectedPeriod && this.selectedPeriod.dateStart > this.selectedPeriod.dateEnd);
    },
    canLoad() {
      return Boolean(
        this.selectedCommunityEcids.length > 0
        && this.selectedPeriod
        && !this.invalidPeriod
        && !this.loading
      );
    },
  },

  actions: {
    setDateRange(value) {
      this.dateRange = Array.isArray(value) ? value : [];
    },

    resetResults() {
      if (this.loading) return;
      this.rows = [];
      this.rowVariants = { billingRelevant: [], all: [] };
      this.hasFetched = false;
      this.error = null;
      this.energyRequestPreview = { requests: [], unresolvedMeterPoints: [], summary: null };
    },

    cacheRows(key, rows) {
      this.cachedRowsByQuery[key] = rows;
      this.cacheOrder = this.cacheOrder.filter((entry) => entry !== key);
      this.cacheOrder.push(key);
      while (this.cacheOrder.length > MAX_CACHE_ENTRIES) {
        const oldestKey = this.cacheOrder.shift();
        delete this.cachedRowsByQuery[oldestKey];
      }
    },

    clearCache() {
      this.cachedRowsByQuery = {};
      this.cacheOrder = [];
    },

    setBillingRelevantMeterCodesOnly(value) {
      this.billingRelevantMeterCodesOnly = Boolean(value);
      this.rows = rowsForMode(this.rowVariants, this.billingRelevantMeterCodesOnly);
      this.energyRequestPreview = { requests: [], unresolvedMeterPoints: [], summary: null };
    },

    async fetchAnalysis(communities, { force = false } = {}) {
      if (!this.canLoad || communities.length !== this.selectedCommunityEcids.length) {
        this.error = 'Die Gemeinschaftsdaten werden noch geladen. Bitte versuchen Sie es erneut.';
        return;
      }

      const period = this.selectedPeriod;
      const key = queryKey(communities, period);
      const requestedSelection = selectionFingerprint(this.selectedCommunityEcids, period);
      this.loading = true;
      this.error = null;
      this.rows = [];

      try {
        if (!force && this.cachedRowsByQuery[key]) {
          this.rowVariants = this.cachedRowsByQuery[key];
          this.rows = rowsForMode(this.rowVariants, this.billingRelevantMeterCodesOnly);
          this.hasFetched = true;
          return;
        }

        let pending = pendingQueries.get(key);
        if (!pending) {
          const dataAnalysisStore = useDataAnalysisStore();
          pending = Promise.all(
            communities.map((community) => fetchCommunityResults(dataAnalysisStore, community, period))
          ).then(buildRowVariants);
          pendingQueries.set(key, pending);
        }

        const resultRows = await pending;
        this.cacheRows(key, resultRows);
        if (selectionFingerprint(this.selectedCommunityEcids, this.selectedPeriod) === requestedSelection) {
          this.rowVariants = resultRows;
          this.rows = rowsForMode(resultRows, this.billingRelevantMeterCodesOnly);
          this.hasFetched = true;
        } else {
          this.rows = [];
          this.rowVariants = { billingRelevant: [], all: [] };
          this.hasFetched = false;
        }
      } catch (caught) {
        this.error = caught?.message || String(caught);
        this.hasFetched = true;
      } finally {
        pendingQueries.delete(key);
        this.loading = false;
      }
    },

    async prepareEnergyRequests(communitiesByEcid, memberships) {
      if (this.rows.length === 0 || this.energyRequestPreviewLoading) return false;
      this.energyRequestPreviewLoading = true;
      try {
        this.energyRequestPreview = await buildEnergyDataRequestsFromRows({
          rows: this.rows,
          communitiesByEcid,
          memberships,
        });
        return (this.energyRequestPreview.summary?.totals?.requestCount ?? 0) > 0;
      } finally {
        this.energyRequestPreviewLoading = false;
      }
    },

    async submitPreparedEnergyRequests() {
      const requests = this.energyRequestPreview.requests;
      if (!requests.length || this.submitting) return null;
      this.submitting = true;
      this.submissionProgress = { current: 0, total: requests.length };
      try {
        return await submitEnergyDataRequests(requests, {
          onProgress: ({ completed, total }) => {
            this.submissionProgress = { current: completed, total };
          },
        });
      } finally {
        this.submitting = false;
      }
    },
  },
});
